// Considered: DialogWrapper / CustomDialogAtom — rejected for the same reason
// `RelationshipSheetMolecule` rejected it: PrimeReact's Dialog portals to `document.body`,
// which puts every datum outside the `side` region and makes the parity walker judge the
// dialog as uncontained. The contract's own shape is a scrim + modal rendered in place
// (`<SampleRecord> > dialog > dialog`), which is what this renders.
//
// Considered: reusing SampleRecordPanelMolecule wholesale — rejected: the contract gives the
// dialog its own datum ids, its own containers, and a different empty-state wording. The
// shared part is the body, which is imported rather than duplicated.
//
// Source: type-composer.jsx:2058-2103 (`<SampleRecord>`) — the rail's `[Full sphere]` opened
// wide, so the same derived record can be read without the rail's width.

import React from 'react';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import { useTranslation } from 'localization';
import type { Schema } from 'types/schema';
import type { AspectRow } from 'types/aspect';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { sampleRecordRows, sampleHealthDrivers, sampleRecordName } from './sampleRecordRows';
import {
  SampleRecordBody,
  DIALOG_DATUMS,
  SandboxSphereActions,
  sandboxCreateDisabled,
  type SandboxSphereReadout,
} from './SampleRecordPanelMolecule';
import './SampleRecordDialogMolecule.scss';

const KEY = 'studio.typeEditor.composer.sideRail';
const CONTAINER = 'side > <SampleRecord> > dialog > dialog';

export interface SampleRecordDialogMoleculeProps {
  visible: boolean;
  definition: Schema | null;
  aspectCatalog?: AspectRow[] | null;
  onHide: () => void;
  /** `[Create as sandbox sphere]` (`:2099`). */
  onCreateSandboxSphere?: () => void;
  /** Removes the sandbox sphere the action created — success's undo. */
  onDeleteSandboxSphere?: () => void;
  /** Where the sandbox action has got to; absent leaves the button in its resting state. */
  sandbox?: SandboxSphereReadout;
}

const SampleRecordDialogMolecule: React.FC<SampleRecordDialogMoleculeProps> = ({
  visible,
  definition,
  aspectCatalog = null,
  onHide,
  onCreateSandboxSphere,
  onDeleteSandboxSphere,
  sandbox,
}) => {
  const { t } = useTranslation();
  const titleId = React.useId();
  const rows = React.useMemo(
    () => sampleRecordRows(definition, aspectCatalog, t),
    [definition, aspectCatalog, t]
  );
  const drivers = React.useMemo(() => sampleHealthDrivers(definition, t), [definition, t]);

  const dialogRef = React.useRef<HTMLDivElement>(null);
  useDialogFocus(visible, dialogRef, { onDismiss: onHide });

  if (!visible) return null;

  return (
    /*
      The scrim closes on click, which is why it carries no keyboard role of its own:
      Close is the keyboard path, and a `role="button"` here would put an unlabelled
      control in the tab order in front of the dialog's own.
    */
    <div
      className="sample-record-dialog__scrim"
      data-datum="side.dialog"
      data-kind="dialog"
      data-container="side > <SampleRecord>"
      data-testid="sample-record-dialog-scrim"
      role="presentation"
      onClick={onHide}
    >
      <div
        className="sample-record-dialog"
        ref={dialogRef}
        data-datum="side.dialog#2"
        data-kind="dialog"
        data-container="side > <SampleRecord> > dialog"
        data-testid="sample-record-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sample-record-dialog__head" data-container={CONTAINER}>
          <strong id={titleId} data-datum="side.recordname" data-kind="strong">
            {sampleRecordName(definition, t)}
          </strong>
          <span className="sample-record-dialog__pill" data-datum="side.t-name#2" data-kind="pill">
            {definition?.name ?? ''}
          </span>
          <span className="sample-record-dialog__caveat" data-datum="side.a-sample-record-not-saved" data-kind="hint">
            {t('studio.typeEditor.sampleRecord.notSaved')}
          </span>
          <ButtonAtom
            variant="text"
            size="small"
            label={t('common.actions.close')}
            onClick={onHide}
            data-datum="side.close"
            data-kind="button"
            data-testid="sample-record-dialog-close-head"
          />
        </div>

        <div className="sample-record-dialog__body" data-container={`${CONTAINER} > group`}>
          <SampleRecordBody
            rows={rows}
            drivers={drivers}
            datums={DIALOG_DATUMS}
            container={`${CONTAINER} > group`}
            emptyText={t(`${KEY}.sampleEmptyShort`)}
          />
        </div>

        <div className="sample-record-dialog__foot" data-container={CONTAINER}>
          <span className="sample-record-dialog__caveat" data-datum="side.nothing-here-is-stored-create-it-as-a-sa" data-kind="hint">
            {t(`${KEY}.sampleNotStored`)}
          </span>
          <ButtonAtom
            variant="secondary"
            size="small"
            label={t('common.actions.close')}
            onClick={onHide}
            data-datum="side.close#2"
            data-kind="button"
            data-testid="sample-record-dialog-close"
          />
          <ButtonAtom
            variant="primary"
            size="small"
            label={t(`${KEY}.createSandboxSphere`)}
            onClick={onCreateSandboxSphere}
            disabled={sandboxCreateDisabled(sandbox)}
            data-datum="side.create-as-sandbox-sphere#2"
            data-kind="button"
            data-testid="sample-record-dialog-create-sandbox"
          />
          <SandboxSphereActions sandbox={sandbox} onDeleteSandboxSphere={onDeleteSandboxSphere} />
        </div>
      </div>
    </div>
  );
};

SampleRecordDialogMolecule.displayName = 'SampleRecordDialogMolecule';

export default SampleRecordDialogMolecule;
